import { Avatar, Box, Chip, Stack, SvgIcon, Theme, Typography, useMediaQuery } from '@mui/material';
import { Variant } from '@mui/material/styles/createTypography';
import { BoxContainer } from '@layouts';
import { ThreeDotsMenu } from '@molecules';
import { CustomStyles } from '@types';
import styles from './styles';

interface SubtitleProps {
  icon?: React.ReactNode;
  text: string;
  variant?: Variant;
}

/**
 * Renders a subtitle with an optional icon and text
 * @param props - The subtitle props
 * @param props.icon - The icon to be displayed before the text
 * @param props.text - The text to be displayed
 * @param props.variant - The typography variant of the text
 * @returns The rendered subtitle
 */
const Subtitle = ({ icon, text, variant = 'body1' }: SubtitleProps) => {
  return (
    <Box sx={styles.subtitleContainer}>
      {icon && <SvgIcon>{icon}</SvgIcon>}
      <Typography variant={variant} sx={styles.subtitle}>
        {text}
      </Typography>
    </Box>
  );
};

interface CardHeaderProps {
  title: string;
  chipLabel: string;
  leftComponentOptions: {
    type: string;
    icon: React.ReactNode;
    customStyle: {
      icon: CustomStyles;
    };
  };
  rightComponentOptions: {
    type: string;
    menuOptions: {
      text: string;
      canViewOption: boolean;
      tooltipText: string;
      icon: React.ReactNode;
      disabled: boolean;
      onClick: () => void;
    }[];
    text: string;
    icon: React.ReactNode;
    onClickIcon: () => void;
  };
  withMenu: boolean;
  subtitleOptions: SubtitleProps | SubtitleProps[];
}
/**
 * Card header component
 */
const CardHeader = ({
  title,
  chipLabel,
  leftComponentOptions,
  rightComponentOptions,
  withMenu,
  subtitleOptions,
}: CardHeaderProps) => {
  const responsive = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  /**
   * @returns the left component depending on the type
   */
  const renderLeftComponent = () => {
    switch (leftComponentOptions.type) {
      case 'avatar':
        return (
          <Avatar sx={styles.avatar}>
            <SvgIcon>{leftComponentOptions.icon}</SvgIcon>
          </Avatar>
        );
      case 'icon':
        return (
          <SvgIcon sx={leftComponentOptions.customStyle?.icon}>{leftComponentOptions.icon}</SvgIcon>
        );
      default:
        return null;
    }
  };

  /**
   * @returns the right component depending on the type
   */
  const renderRightComponent = (renderAll: boolean) => {
    if (
      renderAll &&
      rightComponentOptions &&
      rightComponentOptions.text &&
      rightComponentOptions.menuOptions
    ) {
      return (
        <Stack direction='row' spacing={1}>
          <Box sx={styles.rightTextContainer}>
            <Typography variant='h1'>{rightComponentOptions.text}</Typography>
            {rightComponentOptions.icon && (
              <Box onClick={rightComponentOptions.onClickIcon} sx={styles.iconContainer}>
                <SvgIcon>{rightComponentOptions.icon}</SvgIcon>
              </Box>
            )}
          </Box>
          <ThreeDotsMenu options={rightComponentOptions.menuOptions} />
        </Stack>
      );
    }

    switch (rightComponentOptions.type) {
      case 'threeDotsMenu':
        return <ThreeDotsMenu options={rightComponentOptions.menuOptions} />;
      case 'text':
        return (
          <Box sx={styles.rightTextContainer}>
            <Typography variant='h1'>{rightComponentOptions.text}</Typography>
            {rightComponentOptions.icon && (
              <Box onClick={rightComponentOptions.onClickIcon} sx={styles.iconContainer}>
                <SvgIcon>{rightComponentOptions.icon}</SvgIcon>
              </Box>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  /**
   * @function
   * @description Renders the subtitle with optional icon and text
   * @returns {React.ReactElement | null} The rendered subtitle or null if no subtitle options are provided
   */
  const renderSubtitle = () => {
    if (!subtitleOptions) return null;

    if (Array.isArray(subtitleOptions)) {
      return (
        <Stack spacing={0.5}>
          {subtitleOptions.map((option) => (
            <Subtitle key={option.text} {...option} />
          ))}
        </Stack>
      );
    }
    return <Subtitle icon={subtitleOptions.icon} text={subtitleOptions.text} />;
  };

  return (
    <BoxContainer>
      <Box sx={styles.container(responsive)}>
        <Box sx={styles.leftContainer}>
          {renderLeftComponent()}
          <Box>
            <Box sx={styles.titleContainer(responsive)}>
              <Typography variant='h2'>{title}</Typography>
              {chipLabel && <Chip label={chipLabel} size='small' />}
            </Box>
            {renderSubtitle()}
          </Box>
        </Box>
        {renderRightComponent(withMenu)}
      </Box>
    </BoxContainer>
  );
};

export default CardHeader;
